/**
 * Preço por assento: desconto no banco do meio, prêmio no banco da frente e
 * conforto traseiro (janela paga prêmio, meio é bloqueado).
 *
 * Módulo PURO de propósito — o mesmo raciocínio de `lib/price-bounds.ts`: o
 * jest roda em `testEnvironment: node` e só coleta `__tests__/*.test.ts`, então
 * regra que mora dentro do JSX não tem como ser testada. Nada aqui toca Mongo,
 * Next ou MercadoPago.
 *
 * ---------------------------------------------------------------------------
 * CONVENÇÃO DE DINHEIRO: REAIS INTEIROS
 * ---------------------------------------------------------------------------
 * A plataforma inteira trabalha em reais inteiros — `Math.round` na busca
 * (`app/api/rides/search/route.ts:237`), `RESERVATION_FEE = 5`, `PUBLISH_FIXED_FEE = 10`,
 * `getReservationFee` exibindo `.toFixed(0)`. A spec de produto foi escrita com
 * centavos (R$ 120,40 / R$ 45,15 / R$ 185,15), mas arredondar para inteiro
 * devolve R$ 120 / R$ 45 / R$ 185. Isso NÃO é escolha de design: é o formato
 * do dinheiro deste código. Trocar para centavos atravessaria zod, faixa de
 * preço, taxas e a integração com o MercadoPago — está fora do escopo.
 *
 * Matriz recalculada com arredondamento inteiro:
 *
 *   Caso 1 — A pediu, B recusou, meio não vendido:  140 + 140 + 168     = 448
 *   Caso 2 — conforto ativo:                        185 × 2 + 168        = 538
 *   Caso 3 — sem conforto, janelas ocupadas:        140+140+120+168      = 568
 *   Caso 4 — A pediu, B recusou, meio vendido:      140 × 3 + 168        = 588
 *
 * ---------------------------------------------------------------------------
 * PREÇO DO MEIO É CONDICIONAL
 * ---------------------------------------------------------------------------
 * O desconto só existe quando as duas janelas da fileira estão ocupadas para o
 * MESMO trecho do comprador — se ninguém está sentado do seu lado, não há
 * desconforto a compensar.
 *
 * Enquanto uma tentativa de conforto está VIVA (`pending`) o meio vale CHEIO
 * mesmo com as janelas ocupadas (R1 da spec): o desconto é para compensar
 * desconforto de quem senta no meio, e nesse estado o meio ainda pode virar
 * banco do meio-livre do conforto. Quando o conforto ATIVA o meio é bloqueado
 * (vendável = false), então preço deixa de importar. Quando a tentativa TERMINA
 * (recusa/prazo/meio vendido), os preços voltam à configuração do motorista —
 * `broken` não altera preço (R3), é só diagnóstico; o fechamento efetivo da
 * intenção é a limpeza das flags feita por `breakComfort` em `lib/comfort.ts`.
 */

import { PRICE_ABSOLUTE_FLOOR } from "@/lib/price-bounds"

export interface SeatPricingConfig {
  /** Desconto de 14% no assento do meio, quando as duas janelas estão ocupadas. */
  middleDiscount: boolean
  /** Prêmio de 20% no banco da frente (lado do passageiro). */
  frontPremium: boolean
  /** Conforto traseiro: prêmio dividido entre as duas janelas, meio bloqueado. */
  comfortBack: boolean
}

/**
 * Padrão de uma carona publicada antes desta feature (ou com tudo desligado).
 *
 * Comportamento idêntico ao de hoje: `priceForSeat` devolve o preço do trecho
 * sem tocar em nada. Esse "não muda nada" é uma invariant testada.
 */
export const DEFAULT_SEAT_PRICING: Readonly<SeatPricingConfig> = Object.freeze({
  middleDiscount: false,
  frontPremium: false,
  comfortBack: false,
})

/** −14% → ×0,86. 140 → 120. */
export const MIDDLE_DISCOUNT_FACTOR = 0.86

/** +20% → ×1,20. 140 → 168. */
export const FRONT_PREMIUM_FACTOR = 1.2

/**
 * O prêmio do conforto é 75% do preço do assento do meio, dividido entre as
 * duas janelas. Com 140 como base: 140 × 0,86 = 120,40 → ×0,75 = 90,30 → ÷2 = 45,15.
 * Arredondado para inteiro: 45. Verificado contra a matriz da spec de produto.
 */
export const COMFORT_SHARE_OF_MIDDLE = 0.75

export type SeatCategory =
  /** `front_left` — motorista, nunca é vendido. */
  | "driver"
  /** `front_right` — elegível para o prêmio VIP. */
  | "front_window"
  /** `back_left` / `back_right` (e fileira 3) — elegível ao conforto. */
  | "back_window"
  /** `back_mid` / `row3_mid` — elegível ao desconto. */
  | "middle"

/** Fileira "0" é a dianteira. Só existe `center` nas fileiras de trás. */
export function seatCategory(seat: { id: string; row: number; side: string }): SeatCategory {
  if (seat.id === "front_left") return "driver"
  if (seat.row === 0) return "front_window"
  return seat.side === "center" ? "middle" : "back_window"
}

interface SeatLike {
  id: string
  row: number
  side: string
}

/** Chave de fileira usada em `ride.seatMap.comfortState`. */
export function rowKeyOf(seat: { row: number }): string {
  return String(seat.row)
}

/** Todos os assentos da mesma fileira, na ordem em que foram recebidos. */
export function seatsInRow(seats: ReadonlyArray<SeatLike>, seatId: string): SeatLike[] {
  const target = seats.find((s) => s.id === seatId)
  if (!target) return []
  return seats.filter((s) => s.row === target.row)
}

/**
 * Uma fileira de 3 é a condição de existência do meio e do conforto: precisa
 * ter `left`, `center` E `right`. A fileira dianteira tem só `left` + `right`
 * (o `left` é o motorista), então nunca qualifica — é por isso que conforto e
 * desconto simplesmente não se aplicam ao banco da frente.
 */
export function isRowOfThree(seats: ReadonlyArray<SeatLike>, seatId: string): boolean {
  const row = seatsInRow(seats, seatId)
  return row.some((s) => s.side === "left") && row.some((s) => s.side === "center") && row.some((s) => s.side === "right")
}

/**
 * As duas janelas da fileira estão ocupadas para o trecho em questão?
 *
 * `isOccupied` vem do chamador e já vem escopado ao trecho — em geral é
 * `!seatAvailableForTrecho(...)`. Aqui só importa que as duas pontas existam
 * e estejam ocupadas; o centro é justamente quem não está.
 */
export function rowSidesOccupied(
  seats: ReadonlyArray<SeatLike>,
  seatId: string,
  isOccupied: (seatId: string) => boolean
): boolean {
  // Só existe "meio entre duas janelas" em fileira de três. A dianteira tem
  // `left` + `right` e passaria no teste ingênuo das pontas — mas não tem
  // assento ao qual o desconto se aplique, então não é uma fileira de janelas.
  if (!isRowOfThree(seats, seatId)) return false
  const row = seatsInRow(seats, seatId)
  if (row.length === 0) return false
  const left = row.find((s) => s.side === "left")
  const right = row.find((s) => s.side === "right")
  if (!left || !right) return false
  return isOccupied(left.id) && isOccupied(right.id)
}

/** Contexto do trecho/corrente que decide qual preço vale. */
export interface SeatPriceContext {
  /** As duas janelas da fileira estão ocupadas neste trecho. */
  sidesOccupied?: boolean
  /** Há uma tentativa de conforto viva nesta fileira (aguardando decisão de B). */
  comfortPending?: boolean
  /** O conforto está ativo para ESTA reserva (ela é uma das duas janelas). */
  comfortActive?: boolean
}

function factor(base: number, multiplier: number): number {
  // Dado degenerado não vira dinheiro: devolve o que veio. Só aplica o piso
  // quando há um preço real para trazer para cima dele.
  if (!Number.isFinite(base) || base <= 0) return base
  return Math.max(PRICE_ABSOLUTE_FLOOR, Math.round(base * multiplier))
}

/**
 * Quanto o passageiro paga a mais para o conforto, em reais inteiros.
 *
 * `seg × 0,86 × 0,75 ÷ 2` → 140 → 45. Cada janela paga o seu próprio prêmio a
 * partir do próprio preço de trecho: em multi-trecho A e B podem pagar valores
 * diferentes, generalização que a spec não cobre (ela só pensa em trecho único).
 */
export function comfortSurcharge(seg: number): number {
  if (!Number.isFinite(seg) || seg <= 0) return 0
  return Math.max(0, Math.round(seg * MIDDLE_DISCOUNT_FACTOR * COMFORT_SHARE_OF_MIDDLE * 0.5))
}

/** Preço cheio do conforto: base + prêmio. 140 → 185. */
export function comfortPrice(seg: number): number {
  if (!Number.isFinite(seg) || seg <= 0) return seg
  return Math.max(PRICE_ABSOLUTE_FLOOR, Math.round(seg) + comfortSurcharge(seg))
}

/**
 * Preço do assento para um trecho.
 *
 * `seg` é o preço do TRECHO (`segmentPriceForTrecho` / `computeSegmentPrice`),
 * igual para todos os assentos — o que muda de um assento para o outro é
 * apenas este multiplicador.
 *
 * Com `DEFAULT_SEAT_PRICING` e contexto vazio, devolve `seg` arredondado:
 * nenhuma regra dispara. Essa é a invariant de compatibilidade com caronas
 * publicadas antes da feature.
 */
export function priceForSeat(seg: number, category: SeatCategory, cfg: SeatPricingConfig, ctx: SeatPriceContext = {}): number {
  const base = Number.isFinite(seg) && seg > 0 ? Math.round(seg) : seg

  if (category === "middle") {
    // R1: tentativa viva (pending) → meio cheio (sem desconto). R4 é bloqueio
    // FÍSICO do assento (available:false) — não repassa pelo preço, então
    // conforto ativo não muda o valor exibido (defensivo/diagnóstico).
    if (cfg.middleDiscount && ctx.sidesOccupied && !ctx.comfortPending) {
      return factor(base, MIDDLE_DISCOUNT_FACTOR)
    }
    return base
  }

  if (category === "front_window" && cfg.frontPremium) {
    return factor(base, FRONT_PREMIUM_FACTOR)
  }

  if (category === "back_window" && cfg.comfortBack && ctx.comfortActive) {
    if (!Number.isFinite(base) || base <= 0) return base
    return Math.max(PRICE_ABSOLUTE_FLOOR, base + comfortSurcharge(base))
  }

  return base
}

/** Lê `ride.seatMap.seatPricing` tolerando ausência e dado sujo. */
export function normalizeSeatPricing(raw: unknown): SeatPricingConfig {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_SEAT_PRICING }
  const r = raw as Record<string, unknown>
  const pick = (k: string) => r[k] === true
  return {
    middleDiscount: pick("middleDiscount"),
    frontPremium: pick("frontPremium"),
    comfortBack: pick("comfortBack"),
  }
}

/** `true` quando a carona tem qualquer regra de preço por assento ligada. */
export function hasSeatPricing(cfg: SeatPricingConfig): boolean {
  return cfg.middleDiscount || cfg.frontPremium || cfg.comfortBack
}

// ---------------------------------------------------------------------------
// Estado do Conforto Traseiro
// ---------------------------------------------------------------------------

export type ComfortStatus = "idle" | "pending" | "active"

/**
 * Estado do conforto de UMA fileira, guardado em `ride.seatMap.comfortState[rowKey]`.
 *
 * - `idle`    — ninguém pediu conforto.
 * - `pending` — uma janela (A) pagou o prêmio e está esperando a outra (B)
 *               decidir. O meio CONTINUA VENDÁVEL, porém a preço cheio (R1).
 * - `active`  — as duas janelas optaram/confirmaram; conforto vale para a
 *               viagem e o meio é BLOQUEADO em todos os trechos (R4).
 *
 * `broken` fica `true` para SEMPRE depois que uma tentativa se fechou sem
 * ativar (recusa, prazo, meio vendido). Ele NÃO tem efeito de preço — serve
 * apenas como guarda anti re-arm da reconciliação (senão uma reserva antiga
 * de A re-armaria um pending eterno).
 */
export interface ComfortRowState {
  status: ComfortStatus
  broken: boolean
  /** userIds das janelas que optaram. Usar userId (e não reservationId) permite ler o estado ANTES da reserva existir. */
  optedIn: string[]
  /** Epoch ms. `null` quando não há espera em andamento. */
  holdExpiresAt: number | null
}

export const DEFAULT_COMFORT_ROW: Readonly<ComfortRowState> = Object.freeze({
  status: "idle",
  broken: false,
  optedIn: [],
  holdExpiresAt: null,
})

/** Lê o estado de uma fileira tolerando ausência e dado sujo de Mongo. */
export function normalizeComfortRow(raw: unknown): ComfortRowState {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_COMFORT_ROW }
  const r = raw as Record<string, unknown>
  const status: ComfortStatus = r.status === "pending" || r.status === "active" ? r.status : "idle"
  const optedIn = Array.isArray(r.optedIn) ? r.optedIn.filter((v): v is string => typeof v === "string") : []
  const hold = typeof r.holdExpiresAt === "number" && Number.isFinite(r.holdExpiresAt) ? r.holdExpiresAt : null
  return { status, broken: r.broken === true, optedIn, holdExpiresAt: status === "pending" ? hold : null }
}

/** Estado do conforto da fileira do assento informado. */
export function comfortStateFor(
  seats: ReadonlyArray<SeatLike>,
  ride: { seatMap?: { comfortState?: Record<string, unknown> } } | null | undefined,
  seatId: string
): ComfortRowState {
  const seat = seats.find((s) => s.id === seatId)
  if (!seat) return { ...DEFAULT_COMFORT_ROW }
  const map = ride?.seatMap?.comfortState
  if (!map || typeof map !== "object") return { ...DEFAULT_COMFORT_ROW }
  return normalizeComfortRow(map[rowKeyOf(seat)])
}

/**
 * O contexto de conforto de um assento, pronto para `priceForSeat`.
 *
 * `userId` é opcional e só importa para `comfortActive`: o prêmio é de QUEM
 * optou/confirmou. Sem `userId` (card de busca, preview) basta o status da fileira.
 */
export function comfortContext(
  seats: ReadonlyArray<SeatLike>,
  ride: { seatMap?: { comfortState?: Record<string, unknown> } } | null | undefined,
  seatId: string,
  userId?: string
): { comfortPending: boolean; comfortActive: boolean } {
  const state = comfortStateFor(seats, ride, seatId)
  const comfortPending = state.status === "pending"
  const comfortActive = state.status === "active" && (userId === undefined || state.optedIn.includes(userId))
  return { comfortPending, comfortActive }
}

// ---------------------------------------------------------------------------
// Preço por assento do TRECHO — ponte entre a fórmula pura e quem exibe/cobra
// ---------------------------------------------------------------------------

export interface SeatPricesForTrechoInput {
  /** Assentos do veículo (`ride.seatMap.seats`). */
  seats: ReadonlyArray<SeatLike>
  /** Carona — só para ler `seatMap.comfortState`. */
  ride: { seatMap?: { comfortState?: Record<string, unknown> } } | null | undefined
  /** Preço do trecho, igual para todos os assentos. */
  base: number
  cfg: SeatPricingConfig
  /** Assentos selecionados agora — o lote de um único pagamento. */
  batchSeatIds?: ReadonlyArray<string>
  /** O assento está livre para ESTE trecho, sem considerar o lote. Escopado pelo chamador. */
  isSeatFree: (seatId: string) => boolean
  /** Quem está olhando — decide `comfortActive` (o prêmio é de quem optou). */
  userId?: string
  /**
   * Assentos cujo passageiro marcou "quero conforto" neste lote MAS o pagamento
   * ainda não aconteceu (estado no banco ainda está idle). O prêmio deve
   * aparecer no preço exibido da janela.
   */
  optInByUser?: ReadonlyArray<string>
}

/**
 * O preço de cada assento para o trecho em questão.
 *
 * É o mesmo raciocínio de `computeReservationAmount` (`lib/payments.ts:89`),
 * só que puro e sem query: o servidor é quem cobra, isto é exibição. A única
 * regra que vive aqui (e não em `priceForSeat`) é a do LOTE: quem seleciona as
 * duas janelas de uma fileira num só pagamento vai sentar gente dos dois
 * lados, então o lote conta como ocupado e libera o desconto do meio —
 * espelho de `lib/payments.ts:102`.
 */
export function seatPricesForTrecho(input: SeatPricesForTrechoInput): Record<string, number> {
  const batch = new Set(input.batchSeatIds ?? [])
  const isOccupied = (id: string) => batch.has(id) || !input.isSeatFree(id)
  const optedNow = new Set(input.optInByUser ?? [])
  const out: Record<string, number> = {}
  for (const seat of input.seats) {
    const ctx = comfortContext(input.seats, input.ride, seat.id, input.userId)
    out[seat.id] = priceForSeat(input.base, seatCategory(seat), input.cfg, {
      sidesOccupied: rowSidesOccupied(input.seats, seat.id, isOccupied),
      comfortPending: ctx.comfortPending,
      comfortActive: optedNow.has(seat.id) || ctx.comfortActive,
    })
  }
  return out
}

/**
 * O menor preço entre os assentos LIVRES de um trecho — o "A partir de R$ X"
 * exibido no card de busca. Devolve `null` quando nenhum assento livre foi
 * encontrado (quem chama decide o fallback).
 *
 * `isSeatFree` é escopado ao trecho pelo chamador. Assentos não vendáveis
 * (`available: false`, incluindo o do motorista e o "meio bloqueado" pelo
 * conforto) ficam de fora — o mínimo é sobre o que dá pra comprar.
 */
export function minFreeSeatPrice(
  seats: ReadonlyArray<SeatLike>,
  ride: { seatMap?: { comfortState?: Record<string, unknown> } } | null | undefined,
  base: number,
  cfg: SeatPricingConfig,
  isSeatFree: (seatId: string) => boolean
): number | null {
  const prices = seatPricesForTrecho({ seats, ride, base, cfg, isSeatFree })
  let min: number | null = null
  for (const seat of seats) {
    if (!isSeatFree(seat.id)) continue
    const price = prices[seat.id]
    if (typeof price !== "number") continue
    if (min === null || price < min) min = price
  }
  return min
}
